import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Plus, 
  Search, 
  AlertTriangle, 
  Eye, 
  Trash2, 
  Calendar, 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Filter, 
  X, 
  MoreVertical, 
  GraduationCap, 
  User, 
  RotateCcw, 
  AlertCircle,
  CheckCircle2,
  FileText,
  ChevronDown,
  ShieldAlert,
  Check
} from 'lucide-react';
import CreateAssignmentModal from './CreateAssignmentModal.jsx';
import AssignmentSubmissionsModal from './AssignmentSubmissionsModal.jsx';
import HomeworkDetailModal from './HomeworkDetailModal.jsx';
import homeworkService from '../../services/HomeworkService.js';

const HomeworkAssignmentsManager = ({
  orgData,
  students = [],
  classes = [],
  subjects = [],
  teachers = [],
  onReviewUnassignedStudents
}) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Active Action Menu Dropdown ID
  const [activeMenuId, setActiveMenuId] = useState(null);
  const menuRef = useRef(null);

  // Filter Drawer / Panel State
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Filter Values
  const [filterAcademicYear, setFilterAcademicYear] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDueThisWeek, setFilterDueThisWeek] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // --- Complaints Tab State ---
  const [activeTab, setActiveTab] = useState('homework'); // 'homework' | 'complaints'
  const [complaints, setComplaints] = useState([]);
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [complaintsError, setComplaintsError] = useState(null);
  const [complaintFilterStatus, setComplaintFilterStatus] = useState('');
  const [complaintSearchQuery, setComplaintSearchQuery] = useState('');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showComplaintDetailModal, setShowComplaintDetailModal] = useState(false);
  const [resolvingComplaint, setResolvingComplaint] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState('');
  const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const effectiveOrgId = orgData?.orgId || localStorage.getItem('orgId');
      const res = await homeworkService.getHomework(effectiveOrgId ? { orgId: effectiveOrgId } : {});
      if (res && res.success && res.homework) {
        const mapped = res.homework.map(hw => {
          // Resolve teacher name
          const teacherObj = teachers.find(t => {
            const tid = (t.teacherId || t.id || '').toString();
            const hwTid = (hw.teacherId || '').toString();
            return tid === hwTid || tid === `T${hwTid}` || `T${tid}` === hwTid;
          });
          const teacherName = teacherObj ? (teacherObj.name || teacherObj.teacherName) : (hw.teacherName || hw.teacherId || 'Assigned Faculty');

          // Count class/section students from current database roster
          const classStudents = students.filter(s => {
            const sClass = s.className || s.class || s.currentClass || '';
            const sSection = s.section || s.sectionName || '';
            return sClass.trim() === (hw.className || '').trim() && 
                   (!hw.section || sSection.trim() === (hw.section || '').trim());
          });

          return {
            id: hw.homeworkId.toString(),
            title: hw.title || 'Untitled Homework',
            instructions: hw.instructions || hw.description || '',
            academicYear: hw.semester === 'ALL' || !hw.semester ? '2026-2027' : hw.semester,
            className: hw.className || 'General',
            section: hw.section || 'A',
            subject: hw.subjectName || 'General',
            teacher: teacherName,
            assignedDate: hw.assignedDate || new Date().toISOString().split('T')[0],
            dueDate: hw.dueDate || new Date().toISOString().split('T')[0],
            status: hw.status === 'COMPLETED' ? 'Completed' : (hw.status === 'CANCELLED' ? 'Cancelled' : 'Active'),
            submittedCount: 0,
            totalStudents: classStudents.length > 0 ? classStudents.length : 30
          };
        });

        setAssignments(mapped);

        // Fetch submissions for each homework in parallel to resolve actual submission progress
        Promise.all(res.homework.map(async (hw) => {
          try {
            const subRes = await homeworkService.getSubmissions(hw.homeworkId);
            if (subRes && subRes.success) {
              return {
                id: hw.homeworkId.toString(),
                submittedCount: subRes.submittedCount || 0,
                totalStudents: subRes.totalStudents || (mapped.find(m => m.id === hw.homeworkId.toString())?.totalStudents || 30)
              };
            }
          } catch (e) {
            console.warn(`Could not load submissions for homework ${hw.homeworkId}:`, e);
          }
          return null;
        })).then(results => {
          setAssignments(prev => prev.map(item => {
            const match = results.find(r => r && r.id === item.id);
            if (match) {
              return {
                ...item,
                submittedCount: match.submittedCount,
                totalStudents: match.totalStudents
              };
            }
            return item;
          }));
        });
      } else {
        setAssignments([]);
      }
    } catch (err) {
      console.error('Error loading homework:', err);
      setError('Unable to load homework. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadComplaints = async () => {
    try {
      setLoadingComplaints(true);
      setComplaintsError(null);
      const effectiveOrgId = orgData?.orgId || localStorage.getItem('orgId');
      const res = await homeworkService.getComplaints(effectiveOrgId ? { orgId: effectiveOrgId } : {});
      if (res && res.success && res.complaints) {
        setComplaints(res.complaints);
      } else if (res && res.data) {
        setComplaints(res.data);
      } else {
        setComplaints([]);
      }
    } catch (err) {
      console.error('Failed to load homework doubt complaints:', err);
      setComplaintsError(err.message || 'Failed to load complaints');
    } finally {
      setLoadingComplaints(false);
    }
  };

  const handleOpenResolveModal = (complaint) => {
    setResolvingComplaint(complaint);
    setAdminRemarks('');
    setShowResolveModal(true);
  };

  const handleResolveComplaint = async (e) => {
    e.preventDefault();
    if (!resolvingComplaint || !adminRemarks.trim()) return;
    try {
      setIsSubmittingResolution(true);
      const res = await homeworkService.resolveComplaint(resolvingComplaint.id, {
        adminRemarks: adminRemarks.trim()
      });
      if (res && res.success) {
        setShowResolveModal(false);
        setResolvingComplaint(null);
        setAdminRemarks('');
        if (selectedComplaint && selectedComplaint.id === resolvingComplaint.id) {
          setShowComplaintDetailModal(false);
          setSelectedComplaint(null);
        }
        await loadComplaints();
      } else {
        alert(res?.message || 'Failed to resolve complaint.');
      }
    } catch (err) {
      alert(err.message || 'Failed to resolve complaint.');
    } finally {
      setIsSubmittingResolution(false);
    }
  };

  const pendingComplaintsCount = useMemo(() => {
    return complaints.filter(c => c.status === 'PENDING').length;
  }, [complaints]);

  const filteredComplaints = useMemo(() => {
    return complaints.filter(c => {
      if (complaintFilterStatus && c.status !== complaintFilterStatus) return false;
      if (complaintSearchQuery) {
        const q = complaintSearchQuery.toLowerCase();
        const student = (c.studentName || '').toLowerCase();
        const parent = (c.parentName || '').toLowerCase();
        const teacher = (c.teacherName || '').toLowerCase();
        const subject = (c.subjectName || '').toLowerCase();
        const hwTitle = (c.homeworkTitle || '').toLowerCase();
        const reason = (c.complaintReason || '').toLowerCase();
        const cls = `${c.className || ''}-${c.section || ''}`.toLowerCase();
        if (
          !student.includes(q) &&
          !parent.includes(q) &&
          !teacher.includes(q) &&
          !subject.includes(q) &&
          !hwTitle.includes(q) &&
          !reason.includes(q) &&
          !cls.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [complaints, complaintFilterStatus, complaintSearchQuery]);

  const parseMessagesSnapshot = (snapshotJson) => {
    if (!snapshotJson) return [];
    try {
      const parsed = typeof snapshotJson === 'string' ? JSON.parse(snapshotJson) : snapshotJson;
      return parsed.messages || (Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    loadAssignments();
    loadComplaints();
  }, [orgData?.orgId, students, teachers]);

  // Unassigned Students Count
  const unassignedStudentsCount = useMemo(() => {
    if (!students || students.length === 0) return 0;
    return students.filter(s => {
      const cls = (s.className || s.class || s.currentClass || '').toString().trim();
      return !cls || cls === 'No Class Assigned' || cls === 'Unassigned' || cls === 'N/A' || cls === '0' || cls === 'null' || cls === 'undefined';
    }).length;
  }, [students]);

  // Dynamic Options for Filters
  const availableClasses = useMemo(() => {
    const set = new Set();
    classes.forEach(c => {
      const name = c.className || c.name;
      if (name) set.add(name);
    });
    assignments.forEach(a => {
      if (a.className) set.add(a.className);
    });
    return Array.from(set);
  }, [classes, assignments]);

  const availableSections = useMemo(() => {
    if (!filterClass) {
      return ['A', 'B', 'C', 'D'];
    }
    const matchingClasses = classes.filter(c => (c.className || c.name) === filterClass);
    if (matchingClasses.length > 0) {
      const secs = matchingClasses.map(c => c.section || c.sectionName).filter(Boolean);
      if (secs.length > 0) return Array.from(new Set(secs));
    }
    return ['A', 'B', 'C', 'D'];
  }, [classes, filterClass]);

  const availableSubjects = useMemo(() => {
    const set = new Set();
    subjects.forEach(s => {
      const name = s.subjectName || s.name;
      if (name) set.add(name);
    });
    assignments.forEach(a => {
      if (a.subject) set.add(a.subject);
    });
    if (set.size === 0) {
      return ['Mathematics', 'English', 'Science', 'Social Studies', 'Kannada', 'Hindi', 'Computer Science'];
    }
    return Array.from(set);
  }, [subjects, assignments]);

  // Summary Metrics
  const totalHomeworkCount = assignments.length;
  const activeHomeworkCount = assignments.filter(a => a.status === 'Active').length;
  const pendingSubmissionsCount = assignments.reduce((acc, a) => acc + Math.max(0, a.totalStudents - a.submittedCount), 0);

  // Filtered List
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      if (filterAcademicYear && a.academicYear !== filterAcademicYear) return false;
      if (filterClass && a.className !== filterClass) return false;
      if (filterSection && a.section !== filterSection) return false;
      if (filterSubject && a.subject !== filterSubject) return false;
      if (filterStatus && a.status !== filterStatus) return false;
      
      if (filterDueThisWeek) {
        if (a.status !== 'Active') return false;
        try {
          const due = new Date(a.dueDate);
          const now = new Date();
          const diffDays = (due - now) / (1000 * 60 * 60 * 24);
          if (diffDays < -1 || diffDays > 7) return false;
        } catch {
          // ignore date parse errors
        }
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesTitle = (a.title || '').toLowerCase().includes(q);
        const matchesSubject = (a.subject || '').toLowerCase().includes(q);
        const matchesTeacher = (a.teacher || '').toLowerCase().includes(q);
        const matchesClass = (a.className || '').toLowerCase().includes(q);
        if (!matchesTitle && !matchesSubject && !matchesTeacher && !matchesClass) return false;
      }
      return true;
    });
  }, [assignments, filterAcademicYear, filterClass, filterSection, filterSubject, filterStatus, filterDueThisWeek, searchQuery]);

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filterAcademicYear) count++;
    if (filterClass) count++;
    if (filterSection) count++;
    if (filterSubject) count++;
    if (filterStatus) count++;
    if (filterDueThisWeek) count++;
    return count;
  }, [filterAcademicYear, filterClass, filterSection, filterSubject, filterStatus, filterDueThisWeek]);

  const handleResetFilters = () => {
    setFilterAcademicYear('');
    setFilterClass('');
    setFilterSection('');
    setFilterSubject('');
    setFilterStatus('');
    setFilterDueThisWeek(false);
    setSearchQuery('');
  };

  const handleCreateAssignment = async (newAsn) => {
    try {
      const teacherObj = teachers.find(t => (t.name || t.teacherName) === newAsn.teacher);
      const teacherId = teacherObj ? (teacherObj.teacherId || teacherObj.id) : "1";
      const effectiveOrgId = orgData?.orgId || localStorage.getItem('orgId');

      const payload = {
        title: newAsn.title,
        description: newAsn.instructions || newAsn.title,
        instructions: newAsn.instructions,
        className: newAsn.className,
        section: newAsn.section,
        assignedDate: newAsn.assignedDate,
        dueDate: newAsn.dueDate,
        subjectName: newAsn.subject,
        teacherId: teacherId.toString(),
        orgId: effectiveOrgId
      };

      const res = await homeworkService.createHomework(payload);
      if (res && res.homeworkId) {
        loadAssignments();
      } else {
        alert('Error creating homework: ' + (res?.error || 'Request failed'));
      }
    } catch (e) {
      console.error('Error creating homework:', e);
      alert('Error creating homework: ' + e.message);
    }
  };

  const handleOpenDeleteConfirm = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteModal(true);
    setActiveMenuId(null);
  };

  const handleConfirmDelete = async () => {
    if (!assignmentToDelete) return;
    try {
      setDeletingId(assignmentToDelete.id);
      const res = await homeworkService.deleteHomework(assignmentToDelete.id);
      if (res && res.success) {
        setShowDeleteModal(false);
        setAssignmentToDelete(null);
        if (selectedAssignment?.id === assignmentToDelete.id) {
          setShowDetailModal(false);
          setSelectedAssignment(null);
        }
        loadAssignments();
      } else {
        alert('Error deleting homework: ' + (res?.error || 'Request failed'));
      }
    } catch (e) {
      console.error('Error deleting homework:', e);
      alert('Error deleting homework: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      
      {/* 0. NAVIGATION TABS: ALL HOMEWORK vs PARENT COMPLAINTS */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: '1px solid #E2E8F0',
        paddingBottom: '12px'
      }}>
        <button
          type="button"
          onClick={() => setActiveTab('homework')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 18px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13.5px',
            fontWeight: '700',
            backgroundColor: activeTab === 'homework' ? '#002366' : '#F1F5F9',
            color: activeTab === 'homework' ? '#FFFFFF' : '#64748B',
            transition: 'all 0.15s ease'
          }}
        >
          <BookOpen size={16} />
          All Homework
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab('complaints');
            loadComplaints();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 18px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13.5px',
            fontWeight: '700',
            backgroundColor: activeTab === 'complaints' ? '#002366' : '#F1F5F9',
            color: activeTab === 'complaints' ? '#FFFFFF' : '#64748B',
            transition: 'all 0.15s ease'
          }}
        >
          <AlertCircle size={16} />
          Complaints
          <span style={{
            backgroundColor: pendingComplaintsCount > 0 ? '#DC2626' : (activeTab === 'complaints' ? 'rgba(255,255,255,0.2)' : '#CBD5E1'),
            color: pendingComplaintsCount > 0 || activeTab === 'complaints' ? '#FFFFFF' : '#475569',
            fontSize: '11px',
            fontWeight: '800',
            padding: '2px 7px',
            borderRadius: '10px'
          }}>
            {pendingComplaintsCount}
          </span>
        </button>
      </div>

      {activeTab === 'homework' && (
        <>
      {/* 1. HEADER */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        padding: '18px 24px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.02em' }}>
            Homework
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: '#64748B' }}>
            Create, manage, and track homework across classes.
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            backgroundColor: '#2563EB',
            color: '#FFFFFF',
            border: 'none',
            padding: '10px 18px',
            borderRadius: '10px',
            fontSize: '13.5px',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
            transition: 'background-color 0.15s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
        >
          <Plus size={18} /> + Create Homework
        </button>
      </div>

      {/* 2. ATTENTION / WARNING BANNER (COMPACT) */}
      {unassignedStudentsCount > 0 && (
        <div style={{
          backgroundColor: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderRadius: '10px',
          padding: '10px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#B45309' }}>
            <AlertTriangle size={18} style={{ flexShrink: 0, color: '#D97706' }} />
            <div style={{ fontSize: '13px' }}>
              <strong style={{ color: '#92400E' }}>⚠ {unassignedStudentsCount} students need class assignment</strong>
              <span style={{ color: '#B45309', marginLeft: '6px' }}>
                Assign classes before creating class-based homework.
              </span>
            </div>
          </div>
          {onReviewUnassignedStudents && (
            <button
              onClick={onReviewUnassignedStudents}
              style={{
                backgroundColor: '#D97706',
                color: '#FFFFFF',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Review Students
            </button>
          )}
        </div>
      )}

      {/* 3. SUMMARY KPI CARDS (COMPACT 3-GRID) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '14px'
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          padding: '14px 18px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Total Homework
          </div>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#0F172A', marginTop: '4px' }}>
            {totalHomeworkCount}
          </div>
        </div>

        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          padding: '14px 18px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Active
          </div>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#2563EB', marginTop: '4px' }}>
            {activeHomeworkCount}
          </div>
        </div>

        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          padding: '14px 18px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Pending Submissions
          </div>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#D97706', marginTop: '4px' }}>
            {pendingSubmissionsCount}
          </div>
        </div>
      </div>

      {/* 4. SEARCH & FILTER CONTROLS */}
      <div style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        padding: '14px 18px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          {/* SEARCH BAR */}
          <div style={{ position: 'relative', flex: '1', minWidth: '220px', maxWidth: '400px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '11px', color: '#94A3B8' }} />
            <input
              type="text"
              placeholder="Search homework by title, subject, teacher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                height: '38px',
                paddingLeft: '36px',
                paddingRight: '12px',
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                fontSize: '13px',
                color: '#1E293B',
                outline: 'none',
                backgroundColor: '#F8FAFC',
                boxSizing: 'border-box'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '10px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  padding: 0
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* QUICK TOGGLE & FILTERS BUTTON */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* QUICK STATUS CHIPS */}
            <button
              onClick={() => setFilterStatus(filterStatus === 'Active' ? '' : 'Active')}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                border: filterStatus === 'Active' ? '1px solid #2563EB' : '1px solid #E2E8F0',
                backgroundColor: filterStatus === 'Active' ? '#EFF6FF' : '#FFFFFF',
                color: filterStatus === 'Active' ? '#1D4ED8' : '#64748B'
              }}
            >
              ● Active
            </button>

            <button
              onClick={() => setFilterDueThisWeek(!filterDueThisWeek)}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                border: filterDueThisWeek ? '1px solid #D97706' : '1px solid #E2E8F0',
                backgroundColor: filterDueThisWeek ? '#FEF3C7' : '#FFFFFF',
                color: filterDueThisWeek ? '#B45309' : '#64748B'
              }}
            >
              📅 Due This Week
            </button>

            {/* COMPACT FILTERS TOGGLE */}
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: activeFiltersCount > 0 ? '1px solid #2563EB' : '1px solid #CBD5E1',
                backgroundColor: activeFiltersCount > 0 ? '#EFF6FF' : '#FFFFFF',
                color: activeFiltersCount > 0 ? '#1D4ED8' : '#334155',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Filter size={15} />
              Filters {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
              <ChevronDown size={14} style={{ transform: showFilterPanel ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {activeFiltersCount > 0 && (
              <button
                onClick={handleResetFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748B',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 8px'
                }}
              >
                <RotateCcw size={13} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* EXPANDABLE FILTER PANEL */}
        {showFilterPanel && (
          <div style={{
            padding: '16px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                Academic Year
              </label>
              <select
                value={filterAcademicYear}
                onChange={(e) => setFilterAcademicYear(e.target.value)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
              >
                <option value="">All Academic Years</option>
                <option value="2026-2027">2026–2027</option>
                <option value="2025-2026">2025–2026</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                Class
              </label>
              <select
                value={filterClass}
                onChange={(e) => {
                  setFilterClass(e.target.value);
                  setFilterSection('');
                }}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
              >
                <option value="">All Classes</option>
                {availableClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                Section
              </label>
              <select
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
              >
                <option value="">All Sections</option>
                {availableSections.map(sec => (
                  <option key={sec} value={sec}>Section {sec}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                Subject
              </label>
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
              >
                <option value="">All Subjects</option>
                {availableSubjects.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <button
                onClick={handleResetFilters}
                style={{
                  flex: 1,
                  height: '34px',
                  backgroundColor: '#FFFFFF',
                  color: '#64748B',
                  border: '1px solid #CBD5E1',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                style={{
                  flex: 1,
                  height: '34px',
                  backgroundColor: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 5. CONTENT AREA (LOADING SKELETON / ERROR / CARDS GRID / EMPTY STATES) */}
      {loading ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px'
        }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '14px',
              border: '1px solid #E2E8F0',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              opacity: 0.6
            }}>
              <div style={{ height: '20px', width: '60%', backgroundColor: '#E2E8F0', borderRadius: '4px' }} />
              <div style={{ height: '14px', width: '90%', backgroundColor: '#F1F5F9', borderRadius: '4px' }} />
              <div style={{ height: '32px', width: '100%', backgroundColor: '#F8FAFC', borderRadius: '6px' }} />
              <div style={{ height: '10px', width: '100%', backgroundColor: '#E2E8F0', borderRadius: '4px' }} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          color: '#991B1B'
        }}>
          <AlertCircle size={24} style={{ marginBottom: '8px' }} />
          <div style={{ fontWeight: '700', fontSize: '15px' }}>Unable to load homework.</div>
          <div style={{ fontSize: '13px', marginTop: '4px', color: '#B91C1C' }}>Please try again.</div>
          <button
            onClick={loadAssignments}
            style={{
              marginTop: '12px',
              backgroundColor: '#DC2626',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '14px',
          padding: '48px 24px',
          textAlign: 'center'
        }}>
          {totalHomeworkCount === 0 ? (
            <div>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>📚</div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0F172A' }}>
                No homework found
              </h3>
              <p style={{ margin: '6px 0 16px 0', fontSize: '13.5px', color: '#64748B' }}>
                Create your first homework assignment or adjust your filters.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  backgroundColor: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '9px 18px',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                + Create Homework
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0F172A' }}>
                No homework matches your filters.
              </h3>
              <p style={{ margin: '6px 0 16px 0', fontSize: '13.5px', color: '#64748B' }}>
                Try adjusting your search terms or clearing the selected filters.
              </p>
              <button
                onClick={handleResetFilters}
                style={{
                  backgroundColor: '#F1F5F9',
                  color: '#334155',
                  border: '1px solid #CBD5E1',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        /* 6. RESPONSIVE HOMEWORK CARDS GRID */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px'
        }}>
          {filteredAssignments.map((asn) => {
            const percentage = asn.totalStudents > 0 ? Math.round((asn.submittedCount / asn.totalStudents) * 100) : 0;
            const isMenuOpen = activeMenuId === asn.id;

            return (
              <div
                key={asn.id}
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '14px',
                  padding: '18px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '14px',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                  e.currentTarget.style.borderColor = '#CBD5E1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.02)';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                }}
              >
                {/* CARD TOP ROW: TITLE & STATUS */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                    <h4 
                      style={{ 
                        margin: 0, 
                        fontSize: '15.5px', 
                        fontWeight: '800', 
                        color: '#0F172A',
                        lineHeight: '1.3',
                        wordBreak: 'break-word'
                      }}
                    >
                      {asn.title}
                    </h4>

                    {/* STATUS PILL */}
                    {asn.status === 'Completed' ? (
                      <span style={{
                        backgroundColor: '#ECFDF5',
                        color: '#059669',
                        border: '1px solid #A7F3D0',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        flexShrink: 0
                      }}>
                        <CheckCircle2 size={11} /> Completed
                      </span>
                    ) : asn.status === 'Cancelled' ? (
                      <span style={{
                        backgroundColor: '#F3F4F6',
                        color: '#4B5563',
                        border: '1px solid #E5E7EB',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        Cancelled
                      </span>
                    ) : (
                      <span style={{
                        backgroundColor: '#EFF6FF',
                        color: '#2563EB',
                        border: '1px solid #BFDBFE',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        flexShrink: 0
                      }}>
                        <Clock size={11} /> Active
                      </span>
                    )}
                  </div>

                  {/* SHORT INSTRUCTIONS / SUBTITLE */}
                  {asn.instructions && (
                    <div style={{
                      fontSize: '12.5px',
                      color: '#64748B',
                      marginTop: '6px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: '1.4'
                    }}>
                      {asn.instructions}
                    </div>
                  )}
                </div>

                {/* METADATA PILLS: CLASS • SEC, SUBJECT, DUE DATE */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: '#F1F5F9',
                      color: '#334155',
                      padding: '3px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {asn.className} • Sec {asn.section || 'A'}
                    </span>

                    <span style={{
                      backgroundColor: '#F0FDF4',
                      color: '#15803D',
                      padding: '3px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {asn.subject}
                    </span>
                  </div>

                  <div style={{ fontSize: '12px', color: '#D97706', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '600' }}>
                    <Calendar size={13} color="#D97706" />
                    Due {formatDate(asn.dueDate)}
                  </div>
                </div>

                {/* SUBMISSION PROGRESS BLOCK */}
                <div style={{
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #F1F5F9',
                  borderRadius: '8px',
                  padding: '10px 12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11.5px', fontWeight: '700', marginBottom: '6px' }}>
                    <span style={{ color: '#475569' }}>
                      {asn.submittedCount} of {asn.totalStudents} submitted
                    </span>
                    <span style={{ color: percentage === 100 ? '#10B981' : '#2563EB' }}>
                      {percentage}%
                    </span>
                  </div>
                  
                  <div style={{ width: '100%', height: '6px', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                    <div 
                      style={{ 
                        width: `${percentage}%`, 
                        height: '100%', 
                        backgroundColor: percentage === 100 ? '#10B981' : '#2563EB', 
                        transition: 'width 0.3s ease' 
                      }} 
                    />
                  </div>
                </div>

                {/* CARD FOOTER: TEACHER & ACTIONS */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '6px',
                  borderTop: '1px solid #F1F5F9'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748B' }}>
                    <User size={13} color="#94A3B8" />
                    <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {asn.teacher}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                    <button
                      onClick={() => {
                        setSelectedAssignment(asn);
                        setShowDetailModal(true);
                      }}
                      style={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #CBD5E1',
                        color: '#1E293B',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      View
                    </button>

                    {/* ACTION MENU DROPDOWN TRIGGER */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(isMenuOpen ? null : asn.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#64748B',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="More options"
                    >
                      <MoreVertical size={16} />
                    </button>

                    {/* DROPDOWN MENU */}
                    {isMenuOpen && (
                      <div 
                        ref={menuRef}
                        style={{
                          position: 'absolute',
                          right: 0,
                          bottom: '30px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                          width: '160px',
                          zIndex: 20,
                          overflow: 'hidden'
                        }}
                      >
                        <button
                          onClick={() => {
                            setSelectedAssignment(asn);
                            setShowDetailModal(true);
                            setActiveMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            color: '#334155',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <Eye size={13} /> View Details
                        </button>

                        <button
                          onClick={() => {
                            setSelectedAssignment(asn);
                            setShowSubmissionsModal(true);
                            setActiveMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            color: '#2563EB',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <GraduationCap size={13} /> View Submissions
                        </button>

                        <div style={{ height: '1px', backgroundColor: '#F1F5F9' }} />

                        <button
                          onClick={() => handleOpenDeleteConfirm(asn)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            color: '#DC2626',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 7. DELETE CONFIRMATION MODAL */}
      {showDeleteModal && assignmentToDelete && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(2px)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !deletingId) {
              setShowDeleteModal(false);
              setAssignmentToDelete(null);
            }
          }}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '14px',
            maxWidth: '420px',
            width: '100%',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: '1px solid #E2E8F0'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <Trash2 size={20} />
            </div>

            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
              Delete Homework?
            </h3>
            
            <p style={{ margin: '8px 0 20px 0', fontSize: '13.5px', color: '#64748B', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong style={{ color: '#0F172A' }}>"{assignmentToDelete.title}"</strong>? This action cannot be undone.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                disabled={Boolean(deletingId)}
                onClick={() => {
                  setShowDeleteModal(false);
                  setAssignmentToDelete(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#334155',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={Boolean(deletingId)}
                onClick={handleConfirmDelete}
                style={{
                  padding: '8px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: deletingId ? 'wait' : 'pointer'
                }}
              >
                {deletingId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* ─── COMPLAINTS TAB CONTENT ─── */}
      {activeTab === 'complaints' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* COMPLAINTS HEADER */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '14px',
            padding: '18px 24px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0F172A' }}>
                  Parent Complaints & Escalations
                </h2>
                {pendingComplaintsCount > 0 && (
                  <span style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FCA5A5',
                    color: '#DC2626',
                    fontSize: '11.5px',
                    fontWeight: '700',
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    {pendingComplaintsCount} Pending
                  </span>
                )}
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: '#64748B' }}>
                Review and resolve permanent parent complaint records escalated from homework doubts.
              </p>
            </div>

            <button
              type="button"
              onClick={loadComplaints}
              disabled={loadingComplaints}
              style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #CBD5E1',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#334155',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <RotateCcw size={14} />
              Refresh
            </button>
          </div>

          {/* COMPLAINTS KPI METRICS */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '14px 18px'
            }}>
              <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>
                Total Complaints
              </div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', marginTop: '4px' }}>
                {complaints.length}
              </div>
            </div>

            <div style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '14px 18px'
            }}>
              <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#D97706', textTransform: 'uppercase' }}>
                Pending Review
              </div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#D97706', marginTop: '4px' }}>
                {pendingComplaintsCount}
              </div>
            </div>

            <div style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '14px 18px'
            }}>
              <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#16A34A', textTransform: 'uppercase' }}>
                Resolved
              </div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#16A34A', marginTop: '4px' }}>
                {complaints.filter(c => c.status === 'RESOLVED').length}
              </div>
            </div>
          </div>

          {/* COMPLAINTS FILTER BAR */}
          <div style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            padding: '14px 18px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '220px', maxWidth: '400px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '11px', color: '#94A3B8' }} />
              <input
                type="text"
                placeholder="Search by student, parent, teacher, subject..."
                value={complaintSearchQuery}
                onChange={(e) => setComplaintSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  height: '38px',
                  paddingLeft: '36px',
                  paddingRight: '12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  color: '#1E293B',
                  outline: 'none',
                  backgroundColor: '#F8FAFC',
                  boxSizing: 'border-box'
                }}
              />
              {complaintSearchQuery && (
                <button
                  onClick={() => setComplaintSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '10px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94A3B8',
                    padding: 0
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {['', 'PENDING', 'IN_REVIEW', 'RESOLVED'].map((statusKey) => {
                const label = statusKey === '' ? 'All' : (statusKey === 'PENDING' ? 'Pending' : (statusKey === 'IN_REVIEW' ? 'In Review' : 'Resolved'));
                const isSelected = complaintFilterStatus === statusKey;
                return (
                  <button
                    key={statusKey || 'ALL'}
                    type="button"
                    onClick={() => setComplaintFilterStatus(statusKey)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '12.5px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      border: isSelected ? '1px solid #002366' : '1px solid #E2E8F0',
                      backgroundColor: isSelected ? '#002366' : '#FFFFFF',
                      color: isSelected ? '#FFFFFF' : '#64748B',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* COMPLAINTS TABLE */}
          <div style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontWeight: '700' }}>
                    <th style={{ padding: '12px 16px' }}>Date</th>
                    <th style={{ padding: '12px 16px' }}>Student</th>
                    <th style={{ padding: '12px 16px' }}>Class / Sec</th>
                    <th style={{ padding: '12px 16px' }}>Parent</th>
                    <th style={{ padding: '12px 16px' }}>Subject</th>
                    <th style={{ padding: '12px 16px' }}>Teacher</th>
                    <th style={{ padding: '12px 16px' }}>Homework</th>
                    <th style={{ padding: '12px 16px' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingComplaints ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                        Loading complaints...
                      </td>
                    </tr>
                  ) : filteredComplaints.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={32} style={{ color: '#16A34A' }} />
                          <span style={{ fontWeight: '600' }}>No complaints found</span>
                          <span style={{ fontSize: '12px', color: '#94A3B8' }}>All homework doubts are running smoothly.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredComplaints.map((c) => {
                      const isResolved = c.status === 'RESOLVED';
                      const isInReview = c.status === 'IN_REVIEW';
                      return (
                        <tr
                          key={c.id}
                          style={{
                            borderBottom: '1px solid #F1F5F9',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '12px 16px', color: '#64748B', whiteSpace: 'nowrap' }}>
                            {formatDate(c.createdAt)}
                          </td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: '#0F172A' }}>
                            {c.studentName || c.studentId || 'Student'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>
                            {c.className ? `${c.className}${c.section ? `-${c.section}` : ''}` : 'N/A'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>
                            {c.parentName || 'Parent'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>
                            {c.subjectName || 'General'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>
                            {c.teacherName || 'Teacher'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#002366', fontWeight: '500', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.homeworkTitle || `#${c.homeworkId}`}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11.5px',
                              fontWeight: '700',
                              backgroundColor: isResolved ? '#DCFCE7' : (isInReview ? '#EFF6FF' : '#FEF3C7'),
                              color: isResolved ? '#15803D' : (isInReview ? '#1D4ED8' : '#B45309'),
                              border: `1px solid ${isResolved ? '#BBF7D0' : (isInReview ? '#BFDBFE' : '#FDE68A')}`
                            }}>
                              ● {isResolved ? 'RESOLVED' : (isInReview ? 'IN REVIEW' : 'PENDING')}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedComplaint(c);
                                  setShowComplaintDetailModal(true);
                                }}
                                style={{
                                  backgroundColor: '#EFF6FF',
                                  color: '#1D4ED8',
                                  border: '1px solid #BFDBFE',
                                  borderRadius: '6px',
                                  padding: '5px 10px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <Eye size={13} />
                                View
                              </button>
                              {!isResolved && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenResolveModal(c)}
                                  style={{
                                    backgroundColor: '#16A34A',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <Check size={13} />
                                  Resolve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 8. CREATE HOMEWORK MODAL */}
      <CreateAssignmentModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateAssignment}
        classes={classes}
        subjects={subjects}
        teachers={teachers}
      />

      {/* 9. HOMEWORK DETAIL MODAL */}
      <HomeworkDetailModal
        show={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        onOpenSubmissions={(asn) => {
          setSelectedAssignment(asn);
          setShowSubmissionsModal(true);
        }}
        onDelete={handleOpenDeleteConfirm}
        canDelete={true}
      />

      {/* 10. SUBMISSIONS MODAL */}
      <AssignmentSubmissionsModal
        show={showSubmissionsModal}
        onClose={() => {
          setShowSubmissionsModal(false);
          setSelectedAssignment(null);
          loadAssignments(); // reload to refresh submission counts
        }}
        assignment={selectedAssignment}
        students={students}
      />

      {/* 11. COMPLAINT DETAIL & EVIDENCE MODAL */}
      {showComplaintDetailModal && selectedComplaint && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '680px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }}>
            {/* MODAL HEADER */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F8FAFC'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  borderRadius: '8px',
                  padding: '6px'
                }}>
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0F172A' }}>
                    Complaint #{selectedComplaint.id} Details
                  </h3>
                  <span style={{ fontSize: '12px', color: '#64748B' }}>
                    Reported on {formatDate(selectedComplaint.createdAt)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowComplaintDetailModal(false);
                  setSelectedComplaint(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* MODAL BODY */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* DETAILS GRID */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
                backgroundColor: '#F8FAFC',
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid #E2E8F0'
              }}>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Student</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0F172A' }}>
                    {selectedComplaint.studentName || selectedComplaint.studentId}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Class / Section</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#334155' }}>
                    {selectedComplaint.className} {selectedComplaint.section ? `(${selectedComplaint.section})` : ''}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Parent</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#334155' }}>
                    {selectedComplaint.parentName || 'Parent'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Teacher</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#334155' }}>
                    {selectedComplaint.teacherName || 'Teacher'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Subject</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#334155' }}>
                    {selectedComplaint.subjectName || 'General'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase' }}>Homework</span>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#002366' }}>
                    {selectedComplaint.homeworkTitle || `#${selectedComplaint.homeworkId}`}
                  </div>
                </div>
              </div>

              {/* COMPLAINT REASON BOX */}
              <div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>
                  Parent Complaint Reason
                </span>
                <div style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  color: '#991B1B',
                  fontSize: '13.5px',
                  marginTop: '6px',
                  lineHeight: '1.5'
                }}>
                  "{selectedComplaint.complaintReason}"
                </div>
              </div>

              {/* RESOLUTION REMARKS (IF RESOLVED) */}
              {selectedComplaint.status === 'RESOLVED' && (
                <div style={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '10px',
                  padding: '14px 16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#15803D', fontWeight: '700', fontSize: '13px' }}>
                    <CheckCircle2 size={16} />
                    Complaint Resolved
                  </div>
                  <div style={{ fontSize: '13px', color: '#166534', marginTop: '6px', lineHeight: '1.5' }}>
                    {selectedComplaint.adminRemarks || 'No remarks recorded.'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#15803D', marginTop: '8px' }}>
                    Resolved by <strong>{selectedComplaint.resolvedBy || 'Admin'}</strong> on {formatDate(selectedComplaint.resolvedAt)}
                  </div>
                </div>
              )}

              {/* CONVERSATION SNAPSHOT */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>
                    Preserved Conversation History
                  </span>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                    Permanent Snapshot Evidence
                  </span>
                </div>

                <div style={{
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '10px',
                  padding: '14px',
                  maxHeight: '260px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  {parseMessagesSnapshot(selectedComplaint.doubtSnapshotJson).length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: '13px', padding: '16px' }}>
                      No messages recorded in snapshot.
                    </div>
                  ) : (
                    parseMessagesSnapshot(selectedComplaint.doubtSnapshotJson).map((msg, idx) => {
                      const isTeacher = msg.senderType === 'TEACHER';
                      const isParent = msg.senderType === 'PARENT';
                      return (
                        <div
                          key={idx}
                          style={{
                            alignSelf: isTeacher ? 'flex-end' : 'flex-start',
                            maxWidth: '80%',
                            backgroundColor: isTeacher ? '#002366' : (isParent ? '#FFFBEB' : '#FFFFFF'),
                            color: isTeacher ? '#FFFFFF' : '#1E293B',
                            border: isTeacher ? 'none' : '1px solid #E2E8F0',
                            borderRadius: '10px',
                            padding: '10px 14px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '4px',
                            fontSize: '11px',
                            fontWeight: '700',
                            color: isTeacher ? '#93C5FD' : (isParent ? '#B45309' : '#64748B')
                          }}>
                            <span>{msg.senderName || msg.senderType}</span>
                            <span>•</span>
                            <span style={{ fontSize: '10px', fontWeight: '500' }}>
                              {msg.createdAt ? formatDate(msg.createdAt) : ''}
                            </span>
                          </div>

                          {msg.text && (
                            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                              {msg.text}
                            </div>
                          )}

                          {msg.voiceAttachmentKey && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '6px',
                              fontSize: '11.5px',
                              backgroundColor: isTeacher ? 'rgba(255,255,255,0.15)' : '#F1F5F9',
                              padding: '4px 8px',
                              borderRadius: '6px'
                            }}>
                              🎤 Voice Note {msg.voiceDurationSeconds ? `(${msg.voiceDurationSeconds}s)` : ''}
                            </div>
                          )}

                          {msg.photoAttachmentKey && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '6px',
                              fontSize: '11.5px',
                              backgroundColor: isTeacher ? 'rgba(255,255,255,0.15)' : '#F1F5F9',
                              padding: '4px 8px',
                              borderRadius: '6px'
                            }}>
                              📷 Photo Attached
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div style={{
              padding: '14px 24px',
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              backgroundColor: '#F8FAFC'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowComplaintDetailModal(false);
                  setSelectedComplaint(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#334155',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              {selectedComplaint.status !== 'RESOLVED' && (
                <button
                  type="button"
                  onClick={() => handleOpenResolveModal(selectedComplaint)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#16A34A',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Check size={14} />
                  Resolve Complaint
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 12. RESOLVE COMPLAINT MODAL */}
      {showResolveModal && resolvingComplaint && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '520px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  backgroundColor: '#DCFCE7',
                  color: '#15803D',
                  padding: '6px',
                  borderRadius: '8px'
                }}>
                  <CheckCircle2 size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0F172A' }}>
                  Resolve Parent Complaint
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowResolveModal(false);
                  setResolvingComplaint(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
              Resolving complaint for student <strong>{resolvingComplaint.studentName}</strong> (Homework: <em>{resolvingComplaint.homeworkTitle}</em>).
            </div>

            <form onSubmit={handleResolveComplaint} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                  Resolution Remarks <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="e.g. Spoke with the teacher and parent. The doubt was explained in today's class."
                  value={adminRemarks}
                  onChange={(e) => setAdminRemarks(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    color: '#0F172A',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  disabled={isSubmittingResolution}
                  onClick={() => {
                    setShowResolveModal(false);
                    setResolvingComplaint(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    backgroundColor: '#FFFFFF',
                    color: '#334155',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingResolution || !adminRemarks.trim()}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#16A34A',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: isSubmittingResolution ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Check size={15} />
                  {isSubmittingResolution ? 'Resolving...' : 'Resolve Complaint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeworkAssignmentsManager;
